import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../../lib/apiError';
import { useProfile } from '../../hooks/useProfile';
import { useAuth } from '../../contexts/AuthContext';

export const ChangePasswordCard: React.FC = () => {
  const { changePassword, user } = useProfile();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // No success state: a successful change signs the user out and redirects to
  // the login page, which shows the confirmation there.
  const [error, setError] = useState<string | null>(null);

  // Checked here only to catch a typo before a round trip. The real strength
  // and breach rules live on the server and apply regardless.
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit =
    currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const email = user?.email;
      await changePassword(currentPassword, newPassword);

      // The server ends every session for this account on a password change,
      // including this one. Sign out here and say so, rather than leaving the
      // user on a page that will drop them at the login screen a few minutes
      // later with no explanation.
      await logout();
      navigate('/login', {
        replace: true,
        state: {
          message: 'Your password has been changed. Please sign in with your new password.',
          email,
        },
      });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not change your password.'));
      setIsSaving(false);
    }
  };

  const visibilityToggle = {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={() => setShowPasswords(!showPasswords)} edge="end" disabled={isSaving}>
          {showPasswords ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use at least 8 characters, with an uppercase letter, a lowercase letter, a number, and a
          special character.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Current password"
                type={showPasswords ? 'text' : 'password'}
                fullWidth
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={isSaving}
                InputProps={visibilityToggle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="New password"
                type={showPasswords ? 'text' : 'password'}
                fullWidth
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isSaving}
                InputProps={visibilityToggle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm new password"
                type={showPasswords ? 'text' : 'password'}
                fullWidth
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSaving}
                error={mismatch}
                helperText={mismatch ? 'The two passwords do not match.' : ' '}
                InputProps={visibilityToggle}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button type="submit" variant="contained" disabled={isSaving || !canSubmit}>
              {isSaving ? <CircularProgress size={22} color="inherit" /> : 'Change password'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
